import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreHorizontal, Pencil, Trash2, TrendingUp, TrendingDown, Tags } from "lucide-react";
import { toast } from "sonner";

// Category Dialog
const CategoryDialog = ({ category, onClose }) => {
  const { addCategory, updateCategory } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(category?.name || "");
  const [type, setType] = useState(category?.type || "expense");
  const [loading, setLoading] = useState(false);

  const isEdit = !!category;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        updateCategory(category.id, { name, type });
        toast.success("Category updated!");
      } else {
        addCategory({ name, type });
        toast.success("Category added!");
      }
      setOpen(false);
      setName("");
      if (onClose) onClose();
    } catch (error) {
      toast.error(isEdit ? "Failed to update category" : "Failed to add category");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil className="w-4 h-4 mr-2" />Edit
          </DropdownMenuItem>
        ) : (
          <Button className="rounded-full bg-stone-900 hover:bg-stone-800" data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" />Add Category
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? "Edit Category" : "Add Category"}</DialogTitle>
          <DialogDescription>Create a category to organize your finances.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Category Name</Label>
            <Input placeholder="e.g., Salary, Rent, Food" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType} disabled={isEdit}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
            {isEdit && <p className="text-xs text-stone-400">Category type cannot be changed</p>}
          </div>

          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Category" : "Add Category"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Dialog
const DeleteCategoryDialog = ({ category }) => {
  const [open, setOpen] = useState(false);
  const { deleteCategory } = useApp();

  const handleDelete = () => {
    deleteCategory(category.id);
    toast.success("Category deleted!");
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpen(true); }} className="text-rose-600">
        <Trash2 className="w-4 h-4 mr-2" />Delete
      </DropdownMenuItem>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Category</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete "{category.name}"? This may affect existing transactions.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Category Card
const CategoryCard = ({ category, onNavigate }) => {
  const isIncome = category.type === "income";
  
  return (
    <div
      className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer ${
        isIncome
          ? "bg-emerald-50/50 border-emerald-200 hover:border-emerald-300 hover:shadow-sm"
          : "bg-rose-50/50 border-rose-200 hover:border-rose-300 hover:shadow-sm"
      }`}
      onClick={() => onNavigate(category.id)}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isIncome ? "bg-emerald-100" : "bg-rose-100"}`}>
          {isIncome ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}
        </div>
        <div>
          <span className="font-medium text-stone-900">{category.name}</span>
          <p className="text-xs text-stone-400">Click to view transactions</p>
        </div>
      </div>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <CategoryDialog category={category} />
          <DeleteCategoryDialog category={category} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Empty State
const EmptyState = ({ type }) => (
  <div className="flex flex-col items-center justify-center py-12 text-stone-400">
    <div className="p-4 bg-stone-100 rounded-full mb-4">
      <Tags className="w-8 h-8 text-stone-400" />
    </div>
    <p className="font-medium">No {type ? `${type} ` : ''}categories yet</p>
    <p className="text-sm">Create categories to organize your finances</p>
  </div>
);

export default function Categories() {
  const navigate = useNavigate();
  const { categories } = useApp();
  
  const incomeCategories = categories.filter(c => c.type === "income");
  const expenseCategories = categories.filter(c => c.type === "expense");

  const handleCategoryClick = (categoryId) => {
    navigate(`/categories/${categoryId}`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="categories-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl text-stone-900">Categories</h2>
          <p className="text-stone-500 mt-1">Manage your income and expense categories</p>
        </div>
        <CategoryDialog />
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3 bg-stone-100 p-1 rounded-full">
          <TabsTrigger value="all" className="rounded-full data-[state=active]:bg-white">All ({categories.length})</TabsTrigger>
          <TabsTrigger value="income" className="rounded-full data-[state=active]:bg-white">Income ({incomeCategories.length})</TabsTrigger>
          <TabsTrigger value="expense" className="rounded-full data-[state=active]:bg-white">Expense ({expenseCategories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
            {categories.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2">
                {categories.map((category) => (
                  <CategoryCard key={category.id} category={category} onNavigate={handleCategoryClick} />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="income" className="mt-6">
          <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
            {incomeCategories.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2">
                {incomeCategories.map((category) => (
                  <CategoryCard key={category.id} category={category} onNavigate={handleCategoryClick} />
                ))}
              </div>
            ) : (
              <EmptyState type="income" />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="expense" className="mt-6">
          <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
            {expenseCategories.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2">
                {expenseCategories.map((category) => (
                  <CategoryCard key={category.id} category={category} onNavigate={handleCategoryClick} />
                ))}
              </div>
            ) : (
              <EmptyState type="expense" />
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
